import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { isEmpty } from "lodash";

// Stripe stuff
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import withStyles from "@material-ui/core/styles/withStyles";
import MuiTextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";

import Button from "~/material-dashboard/components/CustomButtons/Button.jsx";
import GridContainer from "~/material-dashboard/components/Grid/GridContainerWithSpacing.jsx";
import GridItem from "~/material-dashboard/components/Grid/GridItemWithSpacing.jsx";

import KickClient from "~/visits/utils/api.js";
import {
  clearConsultAnswersJSON,
  clearOrderItems,
  clearVisitID,
  getOrderComplete,
  getOrderItems,
  getVisitDifferentPharmacy,
  getVisitEmail,
  getVisitFullName,
  getVisitID,
  getVisitPhone,
  setPostCheckoutInfo,
  getVisitProduct,
} from "~/visits/utils/visitCookies.js";
import { useVisitIDRequired } from "~/visits/utils/visitUtils";
import { bugsnagClient } from "~/shared/utils/errorHandling";
import { isConsultationTypeInsomniaInitial } from "~/shared/utils/sharedUtils";

import { CheckoutProgress } from "../shared.jsx";
import {
  SocialProof,
  DoctorGuaranteeSection,
  WhatHappensNext,
  SelectedSleepMeds,
} from "./paymentComponents.jsx";
import {
  checkoutPostData,
  completeCheckout,
  getStripeOptions,
  paymentDetailsFormValidationSchema,
  orderTotalPriceWithCouponDiscount,
  trackCheckout,
} from "./paymentHelpers.js";

import consultationStyles, {
  VISIT_MAX_WIDTH,
} from "~/visits/styles/consultationStyles.js";
import { kickNavy, kickAmaOrange } from "~/shared/styles/baseStyles.js";

let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY, {
      betas: ["link_default_integration_beta_1"],
    });
  }
  return stripePromise;
};

const PaymentDetails = (props) => {
  useVisitIDRequired();
  if (!getVisitID()) {
    window.location.href = "/me/portal";
    return null;
  }
  const { classes } = props;
  const { consultationType } = props.match.params;

  const [creditCardError, setMyCreditCardError] = useState("");
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState("");
  const [{ cardName }, setFormState] = useState({
    cardName: getVisitFullName(),
  });

  // grab setup intent client secret from server
  useEffect(() => {
    // https://devtrium.com/posts/async-functions-useeffect
    if (getOrderComplete()) {
      // if order is complete, don't let user go back to payment page
      props.history.push(`/visits/${consultationType}/payment/complete`);
      window.location.href = `/visits/${consultationType}/payment/complete`;
    }
    console.log("getVisitDifferentPharmacy: ", getVisitDifferentPharmacy());

    const fetchData = async () => {
      // get the client secret from server
      const client = KickClient.newAuthenticatedClient();
      const setupIntentResult = await client.get("/setup-intent");
      console.log("setupIntentResult.data: ", setupIntentResult.data);

      setSetupIntentClientSecret(setupIntentResult.data);
    };

    if (!setupIntentClientSecret) {
      fetchData().catch(console.error);
    }

    if (typeof window.Beacon == "function") {
      // window.Beacon("init", "e67f20b1-8927-4c18-b2a9-3e8119a361ad");
    }
  });

  // show confirmation screen and redirect to payment complete URL
  function showPaymentConfirmationAndRedirect(requiresIDVerification) {
    let redirectURL = `/visits/${consultationType}/payment/complete`;
    if (requiresIDVerification) {
      redirectURL += "?requiresIDVerification=true";
    }
    clearConsultAnswersJSON();
    props.history.push(redirectURL);
  }

  // buyWithInvoice handles both subscription and non-subscription billing
  const buyWithInvoice = async (values, stripe, elements) => {
    const postData = checkoutPostData(values, consultationType);

    const { setupIntent, error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: "https://www.gokick.com/",
      },
      // Uncomment below if you only want redirect for redirect-based payments
      redirect: "if_required",
    });

    if (error) {
      // just a validation error - prevent page from refreshing
      if (error.type == "validation_error") {
        console.log("just return validation error");
        return;
      }

      setFormState(values); // save current form state to re-populate - call after grabbing react values
      setMyCreditCardError(error.message);
      return false;
    }
    const paymentMethodID = setupIntent.payment_method;
    const invoiceID = "";

    const result = await completeCheckout(
      paymentMethodID,
      invoiceID,
      postData,
      setMyCreditCardError
    );

    setFormState(values); // save current form state to re-populate - call after grabbing react values

    // error with attaching card to customer - return
    if (result.error) {
      console.log("[completeCheckout] result error: ", result);
      return;
    }

    // Attempt to fix different pharmacy getting cleared
    if (result.ERROR || result.data?.ERROR) {
      console.error("[completeCheckout]result ERROR || data.ERROR: ", result);
      return;
    }

    if (!result || !result.result) {
      console.error("no result data error: ", result);
      setMyCreditCardError(
        "There was an error processing your visit. Please try again."
      );
      return;
    }

    trackCheckout();

    console.log("[buyWithInvoice] result: ", result);

    setPostCheckoutInfo({
      visitID: getVisitID(),
      orderItems: getOrderItems(),
      product: getVisitProduct(),
      consultationType,
    });

    clearVisitID();
    clearOrderItems();

    showPaymentConfirmationAndRedirect(shouldRedirectToPersona(result));
  };

  function shouldRedirectToPersona(result) {
    // TODO: should we just not check persona on follow ups?
    // Or should we just pull user.persona_verified value from DB before payment?
    let shouldRedirect = true;
    if (result?.result?.data) {
      shouldRedirect = result.result.data.requiresIDVerification;
    } else {
      console.log("wtf is result here: ", result);
    }
    return shouldRedirect;
  }

  const BillingForm = () => {
    const stripe = useStripe();
    const elements = useElements();
    const [paymentRequest, setPaymentRequest] = useState(null); // for apple pay

    const {
      control,
      handleSubmit,
      getValues,
      trigger,
      formState: { isSubmitting, errors },
    } = useForm({
      resolver: yupResolver(paymentDetailsFormValidationSchema),
    });

    // pay using apple or google or msft pay
    const payUsingStripePaymentRequest = async (paymentMethod) => {
      const invoiceID = "";
      const result = await completeCheckout(
        paymentMethod.id,
        invoiceID,
        checkoutPostData(getValues(), consultationType),
        setMyCreditCardError
      );

      setFormState(getValues()); // save current form state to re-populate - call after grabbing react values

      // error with attaching card to customer - return
      if (result.error) {
        console.error("error checking out: ", result);
        return result;
      }

      trackCheckout();

      console.log("[payUsingStripePaymentRequest] result: ", result);

      setPostCheckoutInfo({
        visitID: getVisitID(),
        orderItems: getOrderItems(),
        product: getVisitProduct(),
        consultationType,
      });

      clearVisitID();
      clearOrderItems();

      showPaymentConfirmationAndRedirect(shouldRedirectToPersona(result));

      return result;
    };

    useEffect(() => {
      // hook up Stripe PaymentRequest Button for Google / Apple Pay
      if (stripe) {
        const pr = stripe.paymentRequest({
          country: "US",
          currency: "usd",
          total: {
            label: "",
            amount:
              parseInt(orderTotalPriceWithCouponDiscount(true)) * 100 || 0, // stripe expects value in cents
          },
          requestPayerName: true,
          requestPayerEmail: true,
        });

        // Check the availability of the Payment Request API.
        pr.canMakePayment().then((result) => {
          if (result) {
            console.log("pr.canMakePayment result: ", result);
            setPaymentRequest(pr);

            pr.on("cancel", () => {
              // handle cancel event
            });

            pr.on("paymentmethod", async (event) => {
              const setupResult = await stripe.confirmCardSetup(
                setupIntentClientSecret.clientSecret,
                {
                  payment_method: event.paymentMethod.id,
                }
              );

              console.log(
                "returned confirmCardSetup setupResult:",
                setupResult
              );

              try {
                const kickResult = await payUsingStripePaymentRequest(
                  event.paymentMethod
                );

                if (kickResult.error) {
                  bugsnagClient.user = {
                    email: getVisitEmail(),
                  };
                  bugsnagClient.notify(new Error(kickResult.error));

                  event.complete("fail");
                }

                event.complete("success");
              } catch (e) {
                console.error("Error [payUsingStripePaymentRequest]: ", e);
              }
            });
          }
        });
      }
    }, [stripe]);

    return (
      <form
        onSubmit={handleSubmit(async (data) => {
          console.log(errors);
          try {
            await buyWithInvoice(data, stripe, elements);
          } catch (e) {
            console.log("buyWithInvoice error: ", e);
          }
        })}
        style={{ maxWidth: VISIT_MAX_WIDTH }}
      >
        <div>
          <div>
            <Controller
              render={({ field }) => (
                <MuiTextField
                  {...field}
                  label="Cardholder Name"
                  variant="outlined"
                  fullWidth
                  autoFocus
                />
              )}
              name="cardName"
              control={control}
              defaultValue={cardName}
            />

            <p style={{ color: "red" }}>{errors.cardName?.message}</p>

            <div
              style={{
                border: "1px solid #C4C4C4",
                borderRadius: "4px",
                margin: "6px 0 0 0",
                padding: "20px 8px",
                minWidth: "350px", // requird for mobile
              }}
            >
              <Controller
                name="stripe"
                control={control}
                defaultValue={null}
                render={(field) => (
                  <PaymentElement
                    options={{
                      defaultValues: {
                        billingDetails: {
                          email: getVisitEmail(),
                          name: getVisitFullName(),
                          phone: getVisitPhone(),
                        },
                      },
                    }}
                    {...field}
                  />
                )}
              />
            </div>
            <p
              style={{
                color: "red",
                marginTop: "15px",
                marginBottom: "10px",
                fontSize: "17px",
              }}
            >
              {creditCardError}

              {!isEmpty(errors) && "Please fill out all the fields above"}
            </p>
          </div>

          <DoctorGuaranteeSection classes={classes} />

          <Button
            type="submit"
            className={classes.confirmAndPayButton}
            style={{
              marginTop: "30px",
              fontSize: "18px",
              fontWeight: "bold",
              textTransform: "unset",
              backgroundColor: kickAmaOrange,
              color: kickNavy,
            }}
          >
            Confirm
            {isSubmitting ? (
              <CircularProgress
                size="large"
                style={{
                  position: "relative",
                  left: "24px",
                  top: "1px",
                  color: kickNavy,
                }}
              />
            ) : null}
          </Button>

          {paymentRequest && !isSubmitting && (
            <div className={classes.stripePaymentRequestButtonBlock}>
              <p style={{ textAlign: "center", margin: "0 0 10px 0" }}>
                — or —
              </p>
              <PaymentRequestButtonElement
                options={{ paymentRequest }}
                onClick={async (event) => {
                  // attempt to validate form before showing paymentRequest panel
                  event.preventDefault();
                  const result = await trigger();
                  if (result) {
                    paymentRequest.show();
                  }
                }}
              />
            </div>
          )}
        </div>
      </form>
    );
  };

  if (!setupIntentClientSecret) {
    return null;
  }

  return (
    <Elements
      stripe={getStripe()}
      options={getStripeOptions(setupIntentClientSecret)}
    >
      <div className={`${classes.containerWithSpacing} payment-section`}>
        <div className={classes.checkoutProgressMobile}>
          <CheckoutProgress step={2} />
        </div>
        <GridContainer>
          <GridItem xs={12} md={4}>
            <SocialProof
              style={{ borderBottom: "1px solid #ddd", marginBottom: "24px" }}
              showtestimonials={isConsultationTypeInsomniaInitial(
                consultationType
              ).toString()}
            />
            {isConsultationTypeInsomniaInitial(consultationType) && (
              <>
                <WhatHappensNext />
                <SelectedSleepMeds />
              </>
            )}
          </GridItem>
          <GridItem xs={12} md={8}>
            <div className={classes.paymentForm}>
              <div className={classes.checkoutProgressDesktop}>
                <CheckoutProgress step={2} />
              </div>
              <BillingForm />
            </div>
          </GridItem>
        </GridContainer>
      </div>
    </Elements>
  );
};

export default withStyles(consultationStyles)(PaymentDetails);
